import React from "react";
import { DateTime } from "luxon";
import { TListingValuationTabOutput } from "../../../../domain/codecs/formIO/ListingValuationTabFormIO";
import { THexColor } from "../../models/StringLiterals";
import { CRMCardOutsideLabelled } from "../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { Padding } from "../BuildingBlocks/Padding";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMButtonSecondary } from "../CRMButtonSecondary/CRMButtonSecondary";
import { TListingValuationActionName } from "../../../../domain/codecs/formIO/ListingValuationActionFormIO";
import { MISSING_LABELS, runnableSteps, succeededSteps, TOnValuationAction } from "./valuationTabSteps";

type TStepState = "done" | "current" | "waiting" | "todo";

type TStepControl = { kind: "button"; label: string; name: TListingValuationActionName };

type TStep = {
    label: string;
    state: TStepState;
    detail: string;
    control: TStepControl | null;
};

const formatDate = (isoDate: string): string =>
    DateTime.fromISO(isoDate).setZone("Europe/London").toFormat("d MMM");

const formatAppointment = (iso: string): string =>
    DateTime.fromISO(iso).setZone("Europe/London").toFormat("d MMM, h:mma").replace(":00", "");

const DOT: Record<TStepState, THexColor> = {
    done: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0,
    current: CRMColors.PRIMARY_4,
    waiting: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_0,
    todo: CRMColors.NEUTRAL_10,
};

const buildSteps = (output: TListingValuationTabOutput): Array<TStep> => {
    const runnable = runnableSteps(output.steps);
    const succeeded = succeededSteps(output.steps);
    const failed = output.steps.filter((step) => step.status === "failed");
    const dataIsIn = runnable.length > 0 && succeeded.length === runnable.length;
    const hasPrice = output.market_price_pence !== null;
    const conflicts = output.facts.filter((fact) => fact.conflicts).length;
    const hasAssessment = conflicts === 0;

    return [
        {
            label: "Valuation booked",
            state: output.valuation_date === null ? "todo" : "done",
            detail: output.valuation_date === null
                ? "No date set"
                : formatAppointment(output.valuation_date),
            control: null,
        },
        {
            label: "Data gathered",
            state: dataIsIn
                ? "done"
                : failed.length > 0
                    ? "waiting"
                    : output.steps.length === 0 ? "waiting" : "current",
            detail: output.steps.length === 0
                ? "Nothing collected yet"
                : failed.length > 0
                    ? `Couldn't get ${failed.map((step) => MISSING_LABELS[step.step]).join(" and ")}`
                    : `${succeeded.length} of ${runnable.length} sources in`,
            control: output.steps.length === 0
                ? { kind: "button", label: "Collect the data", name: "start_enrichment" }
                : failed.length > 0
                    ? { kind: "button", label: "Try again now", name: "retry_failed_steps" }
                    : null,
        },
        {
            label: "Facts confirmed",
            state: !dataIsIn ? "todo" : hasAssessment ? "done" : "current",
            detail: conflicts === 0
                ? "Nothing to check"
                : `${conflicts} disagree with the supplier — settle them under Property facts below`,
            control: null,
        },
        {
            label: "Price agreed",
            state: hasPrice ? (output.is_stale ? "current" : "done") : "todo",
            detail: !hasPrice
                ? "Not worked out yet"
                : output.is_stale
                    ? "New information since this was worked out"
                    : "Ready to review",
            control: (!hasPrice && dataIsIn) || output.is_stale
                ? {
                    kind: "button",
                    label: hasPrice ? "Work it out again" : "Work out the price",
                    name: "recompute",
                }
                : null,
        },
        {
            label: "Sent to the vendor",
            state: output.valuation_sent ? "done" : "todo",
            detail: output.valuation_sent
                ? "Sent"
                : !hasPrice
                    ? "Work out the price first"
                    : output.valuation_sent_deferred === null
                        ? "Not sent"
                        : `Snoozed to ${formatDate(output.valuation_sent_deferred)} — it won't send on its own`,
            control: !output.valuation_sent && hasPrice
                ? { kind: "button", label: "Mark as sent", name: "mark_sent" }
                : null,
        },
    ];
};

const Step = (props: {
    step: TStep;
    isLast: boolean;
    isSubmitting: boolean;
    onAction: TOnValuationAction;
}): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.SMALL} childSize="auto 1fr" alignItems="flex-start">
        <SpacingColumn spacing={CRMSpacing.TINY} alignItems="center">
            <Background color={DOT[props.step.state]} width="10px" height="10px" borderRadius="50%" />
            {/* The line between the dots is what makes this read as a sequence rather
                than a list of unrelated facts. */}
            {!props.isLast &&
                <Background color={CRMColors.NEUTRAL_10} width="2px" height="26px" />
            }
        </SpacingColumn>
        <SpacingColumn spacing={CRMSpacing.TINY}>
            <Text size={CRMFontSizes.SMALL}>
                {props.step.state === "current"
                    ? <WeightBold>{props.step.label}</WeightBold>
                    : props.step.label
                }
            </Text>
            <Text
                size={CRMFontSizes.X_SMALL}
                color={props.step.state === "waiting" ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 : CRMColors.NEUTRAL_4}
            >
                {props.step.detail}
            </Text>
            {props.step.control !== null &&
                <CRMButtonSecondary
                    label={props.step.control.label}
                    fullWidth={false}
                    disabled={props.isSubmitting}
                    onClick={() => {
                        if (props.step.control !== null) {
                            props.onAction(props.step.control.name);
                        }
                    }}
                />
            }
        </SpacingColumn>
    </SpacingRow>
);

export const CRMValuationSpine = (props: {
    output: TListingValuationTabOutput;
    isSubmitting: boolean;
    onAction: TOnValuationAction;
}): JSX.Element => {
    const steps = buildSteps(props.output);

    return (
        <CRMCardOutsideLabelled labelColor="highlights-calming-pink-2">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <Text size={CRMFontSizes.MED_PLUS}><WeightBold>This valuation</WeightBold></Text>
                    {steps.map((step, index) =>
                        <Step
                            key={step.label}
                            step={step}
                            isLast={index === steps.length - 1}
                            isSubmitting={props.isSubmitting}
                            onAction={props.onAction}
                        />,
                    )}
                </SpacingColumn>
            </Padding>
        </CRMCardOutsideLabelled>
    );
};
